import { type ReactNode } from "react";
import { useLocalization } from "@fluent/react";
import { Button, IconButton, Snackbar } from "@narmi/design_system";
import type { DualApprovalRejectApproveDialogType } from "./DualApprovalRejectApproveDialog";
import DualApprovalRejectApproveDialog from "./DualApprovalRejectApproveDialog";
import styles from "../DualApproval.module.scss";

type DualApprovalActionBarProps = {
  totalSelected: number;
  isActionLoading: boolean;
  toggledDialogName: DualApprovalRejectApproveDialogType["type"];
  toggleDialog: (name: DualApprovalRejectApproveDialogType["type"]) => void;
  handleAction: (action: "approve" | "reject", note?: string) => void;
  handleDeselectAll: () => void;
  rejectTestId: string;
  approveTestId: string;
  variant?: "payment" | "template";
  hasNoActiveVersion?: boolean;
  children?: ReactNode;
};

const DualApprovalActionBar = ({
  totalSelected,
  isActionLoading,
  toggledDialogName,
  toggleDialog,
  handleAction,
  handleDeselectAll,
  rejectTestId,
  approveTestId,
  variant = "payment",
  hasNoActiveVersion = false,
  children,
}: DualApprovalActionBarProps) => {
  const { l10n } = useLocalization();

  return (
    <>
      <div className={styles.stickyContainer}>
        <Snackbar isActive={Boolean(totalSelected)}>
          {children}
          <IconButton
            kind="action"
            name="x"
            textSize="l"
            label={l10n.getString("label-close")}
            onClick={handleDeselectAll}
            disabled={isActionLoading}
          />
          <Snackbar.Divider />
          <Snackbar.ButtonGroup>
            <Button
              kind="secondary"
              label={l10n.getString("approval-action-reject")}
              testId={rejectTestId}
              onClick={() => toggleDialog("reject")}
              disabled={isActionLoading}
            />
            <Button
              kind="primary"
              label={l10n.getString("approval-action-approve")}
              testId={approveTestId}
              onClick={() => toggleDialog("approve")}
              disabled={isActionLoading}
            />
          </Snackbar.ButtonGroup>
        </Snackbar>
      </div>
      <DualApprovalRejectApproveDialog
        isOpen={Boolean(toggledDialogName)}
        type={toggledDialogName}
        handleClose={() => toggleDialog("")}
        handleReject={(note) => handleAction("reject", note)}
        handleApprove={() => handleAction("approve")}
        totalSelected={totalSelected}
        variant={variant}
        hasNoActiveVersion={hasNoActiveVersion}
      />
    </>
  );
};

export default DualApprovalActionBar;
